/** Upload a batch one file at a time and keep going past a failure.
 *
 * 1 Nov 2026, Job 10189: the office uploader stopped at the first refused file, so one storage hiccup left 31 of 32
 * photos off the Job and the message blamed the file format. A file the server judged (4xx) is reported and not
 * retried; a server or network failure gets one more try after a pause, then is reported by name. */
export type UploadOutcome={saved:number;failed:string[]};
export type PostResult={ok:boolean;status:number};
const RETRY_AFTER_MS=1500;
const retryable=(status:number)=>status>=500||status===408||status===429;
/** A distinct safe route makes API downgrade fail closed instead of accepting
 * an idempotency key that an older server would silently ignore. */
export function officeUploadPath(base:string,ref:string,installation:string,idempotent:boolean){
 return `${base}/api/jobs/${encodeURIComponent(ref)}/${idempotent?'office-photos-idempotent':'office-photos'}/${encodeURIComponent(installation)}`;
}
export async function uploadBatch(
  files:File[],
  post:(file:File,requestId:string)=>Promise<PostResult>,
  prepare:(file:File)=>Promise<File>=async file=>file,
  wait:(ms:number)=>Promise<void>=ms=>new Promise(resolve=>setTimeout(resolve,ms)),
  allowRetry=false,
):Promise<UploadOutcome>{
  const failed:string[]=[];
  for(const file of files){
    const requestId=crypto.randomUUID();
    let prepared:File;
    try{prepared=await prepare(file);}catch{failed.push(file.name);continue;}
    let saved=false;
    for(let attempt=0;attempt<(allowRetry?2:1)&&!saved;attempt++){
      if(attempt)await wait(RETRY_AFTER_MS);
      try{
        const r=await post(prepared,requestId);
        if(r.ok){saved=true;break;}
        if(!retryable(r.status))break; // the server judged the file; a retry cannot change that
      }catch{/* network: one more try */}
    }
    if(!saved)failed.push(file.name);
  }
  return {saved:files.length-failed.length,failed};
}
